import React, { useContext, useEffect, useRef, useState } from "react";
import { Spin, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import ShortcutsTable from "../../Configurations/profile-configuration/shortcuts/ShortcutsTable";
import ShortcutFormModal, {
  type ShortcutFormValues,
} from "../../Configurations/profile-configuration/shortcuts/ShortcutFormModal";
import { Button } from "../../shared";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useIsDevMode } from "../../../hooks/useIsDevMode";
import { useProfileShortcuts } from "../../../hooks/useProfileShortcuts";
import {
  mapProfileShortcuts,
  mapProfileShortcutsToWriteDtos,
} from "../../../queries/profile-shortcuts/mappers";
import { updateProfileShortcutsDto } from "../../../queries/profile-shortcuts/updateProfileShortcuts";
import { withCacheBuster } from "../../../utils/cacheBuster";
import { uploadProfileShortcutIcon } from "../../../utils/profileShortcutIconUpload";
import type { ProfileShortcut } from "../../../types/profileShortcuts";

import FeatureQuickAccessPreview from "./FeatureQuickAccessPreview/FeatureQuickAccessPreview";

import styles from "./FeatureQuickAccess.module.css";

type ConfigurationsAppContext = {
  setActiveSidebarTab: (tab: string, option?: string) => void;
};

/**
 * Fallback icon URL when Create Feature is submitted without a file. Replaced
 * once the admin uploads a real asset (on create or later via Edit).
 */
export const PROFILE_SHORTCUT_CREATE_PLACEHOLDER_ICON =
  "https://placehold.co/64x64/25252f/9ea2b8/png?text=Icon";

/**
 * Feature Quick Access (FE-2349).
 *
 * Wires the FE-2348 `ShortcutsTable`/`ShortcutRow` presentational
 * components into the "Tab Configuration" panel, plus the FE-2349 live
 * `FeatureQuickAccessPreview` mobile mock on the right — a reorder, toggle,
 * edit, or (dev-only) create in the table re-renders that preview's
 * shortcuts row immediately, since both read this same `shortcuts` state.
 *
 * Create Feature is gated by `useIsDevMode` (FEATURE_GUARD_ALLOWED_ENVIRONMENTS).
 * Adding a feature only updates local state + preview (name + id + optional
 * icon upload, else a placeholder); Save Changes persists the full list via
 * `PUT /admin-panel-v2/configurations/profile-shortcuts`.
 *
 * Data comes from `GET /admin-panel-v2/configurations/profile-shortcuts`.
 * Icon picks upload via JSON
 * `POST .../profile-shortcuts/icon` (`shortcut_id` + data-URL `image`).
 * Route access: `configurations.profile` + CORE_APP +
 * `featureFlag: "featureQuickAccess"`.
 */
const FeatureQuickAccess: React.FC = () => {
  usePageTitle(AppStrings.PAM_FEATURE_QUICK_ACCESS);
  const { setActiveSidebarTab } = useContext(
    AppContext,
  ) as ConfigurationsAppContext;
  const isDevMode = useIsDevMode();
  const { shortcuts: configShortcuts, isLoading } = useProfileShortcuts();
  const [shortcuts, setShortcuts] = useState<ProfileShortcut[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState<ProfileShortcut | null>(null);
  // Seeds local state once the config slice finishes loading, without
  // clobbering in-progress reorder/toggle edits on a later config refresh.
  const hasSeededRef = useRef(false);
  // Latest list for Save. Assigning during render is the accepted
  // always-current-ref pattern; React 18 may replay renders, so don't use
  // this for subscriptions or anything that must run once per commit.
  const shortcutsRef = useRef<ProfileShortcut[]>(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    if (!hasSeededRef.current && !isLoading) {
      setShortcuts(configShortcuts);
      hasSeededRef.current = true;
    }
  }, [configShortcuts, isLoading]);

  useEffect(() => {
    setActiveSidebarTab(
      SIDER_TABS.CONFIGURATIONS,
      AppStrings.PROFILE_CONFIGURATION_PAGE_TITLE,
    );
  }, [setActiveSidebarTab]);

  const handleReorder = (reordered: ProfileShortcut[]) => {
    setShortcuts(reordered);
    setIsDirty(true);
  };

  const handleToggleLive = (shortcut: ProfileShortcut, isLive: boolean) => {
    setShortcuts((prev) =>
      prev.map((item) => (item.id === shortcut.id ? { ...item, isLive } : item)),
    );
    setIsDirty(true);
  };

  const handleEdit = (shortcut: ProfileShortcut) => {
    setIsCreateOpen(false);
    setEditingShortcut(shortcut);
  };

  const handleOpenCreate = () => {
    if (!isDevMode) return;
    setEditingShortcut(null);
    setIsCreateOpen(true);
  };

  const handleCloseFormModal = () => {
    if (isUploadingIcon) return;
    setEditingShortcut(null);
    setIsCreateOpen(false);
  };

  const uploadIconIfNeeded = async (
    iconFile: File | null,
    shortcutId: string,
  ): Promise<{ icon: string | null; iconCacheBust?: number } | null> => {
    if (!iconFile) return { icon: null };
    setIsUploadingIcon(true);
    try {
      const icon = await uploadProfileShortcutIcon({
        file: iconFile,
        shortcutId,
      });
      // Upload is keyed by shortcut_id and often returns the same CloudFront
      // path. Persist a `?t=` bust on the stored URL so DEV clients (and a
      // refreshed PAM session) don't keep serving the previous CDN object —
      // poker / p2p invites were the usual victims of that same-URL cache.
      const iconCacheBust = Date.now();
      return {
        icon: withCacheBuster(icon, iconCacheBust),
        iconCacheBust,
      };
    } catch {
      message.error(AppStrings.FAILED_TO_UPDATE_SHORTCUT_ICON);
      return null;
    } finally {
      setIsUploadingIcon(false);
    }
  };

  const handleSubmitCreate = async (values: ShortcutFormValues) => {
    if (!isDevMode || isUploadingIcon || editingShortcut || !values.id) return;

    const id = values.id;
    if (shortcuts.some((item) => item.id === id)) return;

    let nextIcon: string = PROFILE_SHORTCUT_CREATE_PLACEHOLDER_ICON;
    let nextIconCacheBust: number | undefined;
    if (values.iconFile) {
      const uploaded = await uploadIconIfNeeded(values.iconFile, id);
      if (!uploaded?.icon) return;
      nextIcon = uploaded.icon;
      nextIconCacheBust = uploaded.iconCacheBust;
    }

    setShortcuts((prev) => [
      ...prev,
      {
        id,
        name: values.name,
        icon: nextIcon,
        iconCacheBust: nextIconCacheBust,
        order: prev.length + 1,
        isLive: true,
        isDefault: false,
      },
    ]);
    setIsDirty(true);
    setIsCreateOpen(false);
  };

  const handleSubmitEdit = async (values: ShortcutFormValues) => {
    if (!editingShortcut || isUploadingIcon) return;

    let nextIcon = editingShortcut.icon;
    let nextIconCacheBust = editingShortcut.iconCacheBust;
    if (values.iconFile) {
      const uploaded = await uploadIconIfNeeded(
        values.iconFile,
        editingShortcut.id,
      );
      if (!uploaded) return;
      nextIcon = uploaded.icon;
      nextIconCacheBust = uploaded.iconCacheBust;
    } else if (values.clearIcon) {
      nextIcon = null;
      nextIconCacheBust = undefined;
    }

    setShortcuts((prev) =>
      prev.map((item) =>
        item.id === editingShortcut.id
          ? {
              ...item,
              name: values.name,
              icon: nextIcon,
              iconCacheBust: nextIconCacheBust,
            }
          : item,
      ),
    );
    setIsDirty(true);
    setEditingShortcut(null);
  };

  const handleSubmitForm = async (values: ShortcutFormValues) => {
    if (editingShortcut) {
      await handleSubmitEdit(values);
      return;
    }
    await handleSubmitCreate(values);
  };

  const handleSave = async () => {
    if (!isDirty || isSaving || isUploadingIcon) return;
    setIsSaving(true);
    // Snapshot at click time (ref stays aligned with the latest edits).
    const snapshot = shortcutsRef.current;
    try {
      const saved = await updateProfileShortcutsDto(
        mapProfileShortcutsToWriteDtos(snapshot),
      );
      const bustById = new Map(
        snapshot.map((item) => [item.id, item.iconCacheBust]),
      );
      const isDefaultById = new Map(
        snapshot.map((item) => [item.id, item.isDefault]),
      );
      const fromServer = mapProfileShortcuts(saved);
      // Prefer the echo when identity fields match. Icon is excluded: the
      // snapshot URL carries a persisted ?t= bust the server does not echo.
      // If the echo is incomplete or lags the write, keep the snapshot so
      // bulk name edits aren't wiped. isDefault stays local until the wire
      // contract exposes that field.
      const serverMatchesSnapshot =
        fromServer.length === snapshot.length &&
        snapshot.every((item) => {
          const match = fromServer.find((row) => row.id === item.id);
          return (
            !!match &&
            match.name === item.name &&
            match.isLive === item.isLive &&
            match.order === item.order
          );
        });
      setShortcuts(
        (serverMatchesSnapshot ? fromServer : snapshot).map((item) => ({
          ...item,
          iconCacheBust: bustById.get(item.id),
          isDefault: isDefaultById.get(item.id) ?? item.isDefault,
        })),
      );
      setIsDirty(false);
      message.success(AppStrings.PAM_FEATURE_QUICK_ACCESS_SAVE_SUCCESS);
    } catch {
      message.error(AppStrings.PAM_FEATURE_QUICK_ACCESS_SAVE_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className={styles.page} data-testid="feature-quick-access">
        <div className={styles.layout}>
          <div className={styles.content}>
            <section
              className={styles.card}
              data-testid="tab-configuration-card"
            >
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>
                  {AppStrings.PAM_FEATURE_QUICK_ACCESS_TAB_CONFIGURATION}
                </h2>
                {isDevMode ? (
                  <Button
                    intent="primary"
                    iconLeft={<PlusOutlined />}
                    onClick={handleOpenCreate}
                    disabled={isSaving || isUploadingIcon}
                    data-testid="create-feature-button"
                  >
                    {AppStrings.PAM_CREATE_FEATURE}
                  </Button>
                ) : null}
              </header>

              {isLoading ? (
                <div
                  className={styles.loadingState}
                  data-testid="feature-quick-access-loading"
                >
                  <Spin />
                </div>
              ) : (
                <ShortcutsTable
                  shortcuts={shortcuts}
                  onReorder={handleReorder}
                  onToggleLive={handleToggleLive}
                  onEdit={handleEdit}
                />
              )}

              <div className={styles.cardFooter}>
                <Button
                  intent="neutral"
                  disabled={!isDirty || isSaving || isUploadingIcon}
                  onClick={handleSave}
                  data-testid="save-changes-button"
                >
                  {AppStrings.SAVE_CHANGES}
                </Button>
              </div>
            </section>

            <ShortcutFormModal
              open={Boolean(editingShortcut) || isCreateOpen}
              shortcut={editingShortcut}
              existingIds={shortcuts.map((item) => item.id)}
              onClose={handleCloseFormModal}
              onSubmit={handleSubmitForm}
            />
          </div>

          <div className={styles.preview}>
            <section
              className={styles.card}
              data-testid="feature-quick-access-preview"
            >
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>{AppStrings.PREVIEW}</h2>
              </header>
              <FeatureQuickAccessPreview shortcuts={shortcuts} />
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeatureQuickAccess;
